import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';
import axiosInstance from '../../utils/axios';

function DevicePingCountModal({ show, onHide, device }) {
  const [date, setDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  });

  const [selectedDateCount, setSelectedDateCount] = useState(null);
  const [yesterdayCount, setYesterdayCount] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | succeeded | failed
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset local state when modal is closed
    if (!show) {
      const d = new Date();
      setDate(d.toISOString().slice(0, 10));
      setSelectedDateCount(null);
      setYesterdayCount(null);
      setStatus('idle');
      setError(null);
    }
  }, [show]);

  const toISODate = (d) => d.toISOString().slice(0, 10);

  const handleFetch = async () => {
    if (!device?.device_id || !date) return;

    try {
      setStatus('loading');
      setError(null);
      setSelectedDateCount(null);
      setYesterdayCount(null);

      const selectedDate = date;
      const y = new Date(`${selectedDate}T00:00:00.000Z`);
      y.setUTCDate(y.getUTCDate() - 1);
      const yesterdayDate = toISODate(y);

      const [selectedRes, yesterdayRes] = await Promise.all([
        axiosInstance.get(
          `/super-admin/device/${device.device_id}/ping-count?date=${selectedDate}`
        ),
        axiosInstance.get(
          `/super-admin/device/${device.device_id}/ping-count?date=${yesterdayDate}`
        ),
      ]);

      setSelectedDateCount(selectedRes.data?.pingCount ?? 0);
      setYesterdayCount(yesterdayRes.data?.pingCount ?? 0);
      setStatus('succeeded');
    } catch (e) {
      setStatus('failed');
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.msg ||
          e?.message ||
          'Failed to fetch ping counts'
      );
    }
  };

  // When user clicks Ping Count (modal opens), show selected date + yesterday automatically
  useEffect(() => {
    if (show && device?.device_id) {
      handleFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, device?.device_id]);

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Ping Count - {device?.device_id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="mb-3">
          <Form.Group controlId="pingDate">
            <Form.Label>Select Date</Form.Label>
            <Form.Control
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Form.Group>
          <Button
            className="mt-3"
            variant="primary"
            onClick={handleFetch}
            disabled={!device?.device_id || !date || status === 'loading'}
          >
            {status === 'loading' ? 'Fetching...' : 'Get Ping Count'}
          </Button>
        </Form>

        {status === 'loading' && (
          <div className="text-center">
            <Spinner animation="border" role="status" />
            <p className="mt-2">Fetching ping counts...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {status === 'succeeded' && (
          <Alert variant="info" className="mt-3">
            <strong>Date:</strong> {date}
            <br />
            <strong>Total Pings:</strong> {selectedDateCount}
            <hr />
            <strong>Yesterday Total Pings:</strong> {yesterdayCount}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DevicePingCountModal;

